// MACHINERY V2 (Stroje) — catalog browse grid, empty state and loading
// skeleton (M2.8). Split out of CatalogBrowseV2 for the 300-line cap.
//
// Spec: data package §11 "States" — Empty = `noModelsMatchFilters`
// (+Secondary) + Clear when filters are active, `noModelsFound` otherwise.
// Artboards 0900 / 0901 draw the 3-up grid.
//
// THE GRID IS 3-UP AND FIXED HERE. M2.11 owns the tablet 2-up and phone 1-up
// variants ("fleet V1 + catalog -> cols=2" in its row), so this file
// deliberately does NOT read useBreakpoint: adding a half-done responsive rule
// now would be the thing M2.11 has to undo. `auto-fill` with a min track keeps
// it from breaking outright at narrow widths in the meantime.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

/**
 * "zobrazuje sa 1–9 z 6 044" — artboard 0900 puts it directly under the
 * category tabs, above the grid, so that is where it renders (not in the
 * pagination strip). Range from `browseTotalCount` + LOCAL page math, per §11.
 *
 * The artboard's trailing "· zoradené: relevancia" clause is deliberately NOT
 * shipped: there is no sort control on this surface and no endpoint contract
 * that states the ordering, so printing "sorted by relevance" would be an
 * unverified claim about server behaviour (process rule (a)).
 */
export const CatalogShowingLine: React.FC<{
  page: number;
  pageSize: number;
  totalCount: number;
}> = ({ page, pageSize, totalCount }) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const lang = getCurrentLanguage();
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        color: "var(--faint)",
        letterSpacing: "0.04em",
        marginBottom: 10,
        minWidth: 0,
      }}
    >
      {translate("myMachinery:showingResults", {
        from: from.toLocaleString(lang),
        to: to.toLocaleString(lang),
        total: totalCount.toLocaleString(lang),
      })}
    </div>
  );
};

export const CatalogGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
      gap: 14,
      alignItems: "stretch",
      minWidth: 0,
    }}
  >
    {children}
  </div>
);

export const CatalogSkeletonGrid: React.FC<{ cards?: number }> = ({ cards = 9 }) => (
  <CatalogGrid>
    {Array.from({ length: cards }, (_, i) => (
      <div
        key={i}
        aria-hidden
        style={{
          height: 196,
          borderRadius: 10,
          background: "var(--card-2)",
          border: "1px solid var(--border-soft)",
        }}
      />
    ))}
  </CatalogGrid>
);

export const CatalogEmpty: React.FC<{ hasFilters: boolean; onClear: () => void }> = ({
  hasFilters,
  onClear,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        padding: "56px 20px",
        borderRadius: 10,
        border: "1px dashed var(--border)",
        background: "var(--card)",
        textAlign: "center",
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-2)" }}>
        {translate(
          hasFilters ? "myMachinery:noModelsMatchFilters" : "myMachinery:noModelsFound"
        )}
      </div>
      {hasFilters && (
        <React.Fragment>
          <div style={{ fontSize: 12.5, color: "var(--muted)", maxWidth: 380 }}>
            {translate("myMachinery:noModelsMatchFiltersSecondary")}
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClear}
            style={{ marginTop: 4 }}
          >
            {translate("myMachinery:clearAll")}
          </button>
        </React.Fragment>
      )}
    </div>
  );
};
